import { useEffect, useState, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { request } from "hooks/useOkoApi/useOkoApi";
import {
  loadFilters,
  loadSharedFilters,
  resolveClassId,
} from "utils/persistFilters";

// Educator class list endpoint has two variants depending on context (impersonation
// vs. signed-in educator); both can return an array OR an object with a `classes` key.
const fetchClassList = async (educatorId) => {
  if (educatorId) {
    const data = await request(
      `class-report/educator/${educatorId}/classes`,
      { method: "GET" }
    );
    return Array.isArray(data) ? data : data?.classes || [];
  }
  const educatorData = await request("account/educator", { method: "GET" });
  return educatorData?.classes || [];
};

// One cached class list for every report page (OKO2-3684): each of the report
// tabs used to refetch this on mount, so hopping between them meant a spinner
// per page. Classes change rarely, so the app-default 5-minute staleTime is
// safe here — within it fetchQuery returns the cached list instantly and
// dedupes concurrent callers.
export const getReportClassList = (queryClient, educatorId) =>
  queryClient.fetchQuery({
    queryKey: ["report-class-list", educatorId ?? null],
    queryFn: () => fetchClassList(educatorId),
  });

// Centralizes the "which class + filter snapshot do we start with" dance that every
// educator report page needs: fetch classes, honor a parent-provided `sharedClassId`,
// fall back to `resolveClassId` (which consults the shared filter and latest session),
// and expose `savedState` / `sharedState` so the calling page can initialize its
// page-specific fields while honoring the "shared is authoritative when the class is
// in focus" rule.
export const useFilterPersistence = ({
  pageKey,
  educatorId,
  sharedClassId,
  onClassChange,
}) => {
  const [classes, setClasses] = useState([]);
  const [selectedClassId, setSelectedClassIdInternal] = useState(null);
  const [savedState, setSavedState] = useState(null);
  const [sharedState, setSharedState] = useState(null);
  const [filtersReady, setFiltersReady] = useState(false);
  // `hydrated` gates caller-side persistence. Filter fields live on the caller
  // (pages have different sets), so the caller must signal when it has applied
  // savedState/sharedState into its own state. Without this, the render where
  // `filtersReady` flips to true but fields are still at their initial nulls
  // would fire a persist and clobber the shared snapshot we just hydrated from.
  const [hydrated, setHydrated] = useState(false);
  const [error, setError] = useState(null);
  const queryClient = useQueryClient();

  useEffect(() => {
    let cancelled = false;
    const resolve = async () => {
      try {
        const classList = await getReportClassList(queryClient, educatorId);
        if (cancelled) return;
        setClasses(classList);
        if (classList.length === 0) return;

        let resolvedClassId;
        let saved = null;
        let shared = null;
        if (sharedClassId && classList.some((c) => c.id === sharedClassId)) {
          resolvedClassId = sharedClassId;
          saved = loadFilters(pageKey);
          const maybeShared = loadSharedFilters();
          // Only treat shared as authoritative when it belongs to this class.
          shared = maybeShared?.selectedClassId === sharedClassId ? maybeShared : null;
        } else {
          const r = await resolveClassId(pageKey, classList);
          if (cancelled) return;
          resolvedClassId = r.classId;
          saved = r.saved || null;
          // resolveClassId only returns shared when it validated it for the
          // current class list; don't fall back to loadSharedFilters here or
          // we could resurrect a stale snapshot from a different class.
          shared = r.shared || null;
        }
        setSavedState(saved);
        setSharedState(shared);
        setSelectedClassIdInternal(resolvedClassId);
        if (onClassChange) onClassChange(resolvedClassId);
      } catch (e) {
        if (!cancelled) setError(e);
      } finally {
        if (!cancelled) setFiltersReady(true);
      }
    };
    resolve();
    return () => {
      cancelled = true;
    };
    // sharedClassId/pageKey/onClassChange are consumed only on initial resolution;
    // re-resolving on their change would fight the page's own state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [educatorId]);

  const setSelectedClassId = useCallback(
    (id) => {
      setSelectedClassIdInternal(id);
      if (onClassChange) onClassChange(id);
    },
    [onClassChange]
  );

  const markHydrated = useCallback(() => setHydrated(true), []);

  const hasSharedContext = Boolean(sharedState?.selectedClassId);
  const readyToPersist = filtersReady && hydrated;

  return {
    classes,
    selectedClassId,
    setSelectedClassId,
    savedState,
    sharedState,
    hasSharedContext,
    filtersReady,
    hydrated,
    markHydrated,
    readyToPersist,
    error,
  };
};
